import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, AlertCircle } from 'lucide-react';
import { api, ApiError } from '@/api';
import LocaleSwitch from '@shared/ui/LocaleSwitch.jsx';

/**
 * Request erasure of an account and everything attached to it.
 *
 * Public and unauthenticated on purpose: Google Play requires a deletion URL
 * reachable without signing in, and someone who has lost access to their
 * mailbox cannot sign in to ask. That also means the response can never say
 * whether an account exists — an unauthenticated endpoint that confirmed
 * "yes, this address is registered" would be an attendee-list oracle.
 *
 * The request is recorded for the organisers to carry out, not executed on the
 * spot. Erasure takes the ticket, the bookings and the 1:1 slots with it, and a
 * mistyped address doing that silently is worse than a day's wait.
 */
export default function DeleteAccount() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | done | error
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    setError(null);
    try {
      await api.account.requestDeletion(email.trim(), note.trim() || undefined);
      setState('done');
    } catch (err) {
      console.error('[delete-account] request failed', err);
      setError(err instanceof ApiError && err.code === 'invalid_email' ? t('deleteAccount.invalidEmail') : t('common.loadFailed'));
      setState('error');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-black border-b border-white/10">
        <div className="h-1 w-full bg-brand" />
        <div className="site-shell px-5 py-3 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-sm label-tag transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t('microsite.backHome')}
          </Link>
          <LocaleSwitch />
        </div>
      </header>

      <main className="site-shell px-5 py-10 max-w-[38rem]">
        <h1 className="display-lg text-white text-4xl mb-4">{t('deleteAccount.title')}</h1>
        <p className="text-white/60 text-sm leading-relaxed font-body mb-8">{t('deleteAccount.intro')}</p>

        <section className="mb-8">
          <h2 className="font-heading font-bold uppercase text-brand text-base mb-3">
            {t('deleteAccount.whatTitle')}
          </h2>
          <ul className="space-y-1.5">
            {['registration', 'agenda', 'meetings', 'checkin', 'notifications'].map((key) => (
              <li key={key} className="text-white/60 text-sm leading-relaxed font-body flex gap-2">
                <span className="text-brand shrink-0">—</span>
                {t(`deleteAccount.what.${key}`)}
              </li>
            ))}
          </ul>
          <p className="text-white/40 text-xs leading-relaxed font-body mt-4">{t('deleteAccount.keepsNote')}</p>
        </section>

        {state === 'done' ? (
          <div className="rounded-xl border border-brand/40 bg-brand/5 px-5 py-6">
            <p className="flex items-center gap-2 font-heading font-bold uppercase text-brand text-lg">
              <Check className="w-5 h-5" /> {t('deleteAccount.doneTitle')}
            </p>
            <p className="text-white/60 text-sm leading-relaxed font-body mt-2">{t('deleteAccount.doneBody')}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-tag text-white/40 text-[10px] block mb-2" htmlFor="email">
                {t('deleteAccount.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-base
                           font-body focus:outline-none focus:border-brand/50 placeholder:text-white/20"
              />
              <p className="text-white/30 text-xs font-body mt-2">{t('deleteAccount.emailHint')}</p>
            </div>

            <div>
              <label className="label-tag text-white/40 text-[10px] block mb-2" htmlFor="note">
                {t('deleteAccount.noteLabel')}
              </label>
              <textarea
                id="note"
                rows={3}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm
                           font-body focus:outline-none focus:border-brand/50"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-destructive/10 border border-destructive/30 px-3 py-2.5">
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-destructive text-xs font-body">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={state === 'sending'}
              className="w-full py-4 rounded-xl bg-brand text-black font-heading font-black uppercase text-lg
                         disabled:opacity-40"
            >
              {state === 'sending' ? t('deleteAccount.sending') : t('deleteAccount.submit')}
            </button>
          </form>
        )}

        <p className="text-white/30 text-xs leading-relaxed font-body mt-10 pt-6 border-t border-white/10">
          {t('deleteAccount.privacyNote')}{' '}
          <Link to="/privacy" className="text-brand underline underline-offset-2">
            {t('microsite.privacyLink')}
          </Link>
        </p>
      </main>
    </div>
  );
}
